import React from "react";
import EvidenceListView, { ListRow } from "./EvidenceListView";

const fyLabel = (id, financialYears) => financialYears.find((fy) => String(fy.id) === String(id))?.financial_year_value || "—";

/** Sub-location is the real entity when set; the parent location is only
 *  the aggregating record otherwise. */
const locationLabel = (batch, sources) => {
  const src = sources.find((s) => String(s.id) === String(batch?.sourceId));
  if (!src) return "—";
  const base = src.unitCode ? src.unitCode : `${src.location?.city} - ${src.location?.state}`;
  const sub = (src.subLocation || []).find((s) => String(s.id) === String(batch?.subLocationId));
  return sub ? `${base} / ${sub.subLocation}` : base;
};

/** First-uploaded document for a batch, by lowest document id — used to show
 *  a real filename instead of the batch's own name, matching the Evidences
 *  tab's row style. `documents` is the flat, unfiltered quantitative list so
 *  the label stays stable regardless of the active search/tag filters. */
const firstDocumentFor = (batch, documents) => {
  const inBatch = documents.filter((d) => String(d.documentsBatchId) === String(batch.id));
  if (!inBatch.length) return null;
  return inBatch.reduce((a, b) => (a.id < b.id ? a : b));
};

/** No batch-level verifiedCount from the backend — derived from the flat document list. */
const verifiedCountFor = (batch, documents) =>
  documents.filter((d) => String(d.documentsBatchId) === String(batch.id) && d.isVerified).length;

const fmtDate = (d) => {
  if (!d) return "—";
  try { return new Date(d).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }); }
  catch { return d; }
};

const COLUMNS = [
  { key: "name", label: "Name", flex: 2 },
  { key: "fy", label: "Financial Year", width: 120 },
  { key: "location", label: "Location", width: 150 },
  { key: "uploadedBy", label: "Uploaded By", width: 130 },
  { key: "grouping", label: "Grouping", width: 110 },
  { key: "counts", label: "Files / Parsed / Reviewed", width: 220 },
  { key: "verification", label: "Verification", width: 150 },
  { key: "created", label: "Uploaded", width: 110 },
  { key: "chevron", label: "", width: 30 },
];

/**
 * "Uploaded Documents" (Document Management) tab — every Documents Batch,
 * reviewed or not, straight off GET /documents/batches. One row per batch —
 * no client-side grouping needed, unlike the Evidences tab.
 */
const UploadedDocumentsListView = ({ batches, documents, sources, financialYears, isLoading, error, onRetry, onRowClick }) => (
  <EvidenceListView
    columns={COLUMNS}
    rows={batches}
    rowKey={(b) => b.id}
    isLoading={isLoading}
    error={error}
    onRetry={onRetry}
    emptyTitle="No uploaded batches"
    emptySub="Batches you upload appear here, whether reviewed yet or not."
    footer={<span>Showing {batches.length} batch{batches.length === 1 ? "" : "es"}</span>}
    renderRow={(batch, index) => {
      const firstDoc = firstDocumentFor(batch, documents);
      const label = firstDoc?.fileName || batch.name || `Batch #${batch.id}`;
      const fileCount = batch.documentCount ?? 0;
      const verifiedCount = verifiedCountFor(batch, documents);
      const unverifiedCount = fileCount - verifiedCount;
      return (
      <ListRow index={index} onClick={() => onRowClick({ type: "batch", batchId: batch.id, label })}>
        <div style={{ flex: 2, padding: "0 10px", minWidth: 0 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: "#1e293b", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={label}>
            {label}
          </div>
          <div style={{ fontSize: 11, color: "#94a3b8" }}>{fileCount.toLocaleString("en-IN")} file{fileCount === 1 ? "" : "s"}</div>
        </div>
        <div style={{ width: 120, padding: "0 10px", fontSize: 12, color: "#475569" }}>{fyLabel(batch.financialYearId, financialYears)}</div>
        <div style={{ width: 150, padding: "0 10px", fontSize: 12, color: "#475569", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {locationLabel(batch, sources)}
        </div>
        <div style={{ width: 130, padding: "0 10px", fontSize: 12, color: "#475569", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {firstDoc?.uploadedByName || "—"}
        </div>
        <div style={{ width: 110, padding: "0 10px" }}>
          <span style={{ fontSize: 10, fontWeight: 700, padding: "3px 9px", borderRadius: 20, background: batch.grouped === false ? "#fff7ed" : "#f5f3ff", color: batch.grouped === false ? "#c2410c" : "#7c3aed", border: `1px solid ${batch.grouped === false ? "#fed7aa" : "#ddd6fe"}` }}>
            {batch.grouped === false ? "Ungrouped" : "Grouped"}
          </span>
        </div>
        <div style={{ width: 220, padding: "0 10px", display: "flex", gap: 6, flexWrap: "wrap" }}>
          <span style={{ fontSize: 10, fontWeight: 700, padding: "3px 9px", borderRadius: 20, background: "#f1f5f9", color: "#475569" }}>{(batch.documentCount ?? 0).toLocaleString("en-IN")} files</span>
          <span style={{ fontSize: 10, fontWeight: 700, padding: "3px 9px", borderRadius: 20, background: "#e0f2fe", color: "#0369a1" }}>{(batch.parsedCount ?? 0).toLocaleString("en-IN")} parsed</span>
          <span style={{ fontSize: 10, fontWeight: 700, padding: "3px 9px", borderRadius: 20, background: "#fef9c3", color: "#854d0e" }}>{(batch.reviewedCount ?? 0).toLocaleString("en-IN")} reviewed</span>
        </div>
        <div style={{ width: 150, padding: "0 10px", display: "flex", gap: 6, flexWrap: "wrap" }}>
          {verifiedCount > 0 && (
            <span style={{ fontSize: 10, fontWeight: 700, padding: "3px 9px", borderRadius: 20, background: "#dcfce7", color: "#166534", whiteSpace: "nowrap" }}>
              <i className="fas fa-check-circle" style={{ marginRight: 4 }} />{verifiedCount.toLocaleString("en-IN")} verified
            </span>
          )}
          {unverifiedCount > 0 && (
            <span style={{ fontSize: 10, fontWeight: 700, padding: "3px 9px", borderRadius: 20, background: "#fff7ed", color: "#c2410c", whiteSpace: "nowrap" }}>
              <i className="fas fa-exclamation-circle" style={{ marginRight: 4 }} />{unverifiedCount.toLocaleString("en-IN")} unverified
            </span>
          )}
        </div>
        <div style={{ width: 110, padding: "0 10px", fontSize: 11.5, color: "#94a3b8" }}>{fmtDate(batch.createdAt)}</div>
        <div style={{ width: 30, display: "flex", justifyContent: "center" }}>
          <i className="fas fa-chevron-right" style={{ fontSize: 11, color: "#cbd5e1" }} />
        </div>
      </ListRow>
      );
    }}
  />
);

export default UploadedDocumentsListView;
